import { useCallback, useEffect, useState } from 'react';
import {
  maakSepaBestand,
  berekenInkoopkosten,
  berekenMarge,
  evenementdagen,
  type Evenementafrekening,
  type Marge,
  type Factuur,
  type KraamafrekeningInvoer,
  type Partij,
  type SepaBestand,
  type Uitbater,
  type BtwRegel,
  type BtwTarief,
  berekenKraamTransactiekosten,
  maakEvenementafrekening,
} from '@qc/core';
import type { Evenement } from '@qc/db';
import { supabase } from '../supabase.js';
import { haalAlles } from '../paginering.js';
import { logAudit } from '../audit.js';

/**
 * Onze eigen gegevens komen uit de database, niet uit deze code.
 *
 * Hier stond een KvK-nummer van 00000000 met een testIBAN. Dat is erger dan
 * geen factuur: het is een ongeldige factuur met een vreemd rekeningnummer,
 * verstuurd naar een echte klant. Een beheerder vult ze in bij Beheer.
 */
async function haalOnzeGegevens(): Promise<Partij> {
  const { data } = await supabase.from('bedrijfsgegevens').select('*').maybeSingle();
  const adres = [data?.adres, [data?.postcode, data?.plaats].filter(Boolean).join(' ')]
    .filter((deel) => deel != null && deel.trim() !== '')
    .join(', ');

  return {
    naam: data?.naam ?? '',
    kvkNummer: data?.kvk_nummer ?? null,
    btwNummer: data?.btw_nummer ?? null,
    iban: data?.iban ?? null,
    adres: adres === '' ? null : adres,
  };
}

/** Een factuur zoals vaststel-facturen 'm teruggeeft: met een echt nummer en een plek in Storage. */
export interface VastgesteldeFactuur {
  id: string;
  status: string;
  verstuurdOp: string | null;
  pdfPad: string | null;
  kraamdeelnameId: string | null;
  organisatorId: string | null;
  factuur: Factuur;
}

/** Een uitbetaling zoals vaststel-facturen 'm teruggeeft: bedrag bevroren op het moment van vaststellen. */
export interface VastgesteldeUitbetaling {
  id: string;
  partij: 'kraam' | 'organisator';
  kraamdeelnameId: string | null;
  organisatorId: string | null;
  naam: string;
  iban: string;
  bedragCent: number;
  status: string;
  verstuurdOp: string | null;
}

export interface Afrekeningbeeld {
  laden: boolean;
  fout: string | null;
  /** De afrekening klopt, maar er kan geen factuur van worden gemaakt. */
  factuurfout: string | null;
  afrekening: Evenementafrekening | null;
  /** Wat Quality Cashless aan dit evenement overhoudt. */
  marge: Marge | null;
  inkoopTransactiekostenCent: number;
  facturen: VastgesteldeFactuur[];
  /** E-mailadres(sen) per factuurnummer; ontbreekt als de partij geen e-mail heeft. */
  ontvangerPerFactuur: Record<string, string[]>;
  sepa: SepaBestand | null;
  uitbetalingen: VastgesteldeUitbetaling[];
  /** Wanneer deze batch voor het eerst als "onderweg naar de bank" is gemarkeerd, of null. */
  batchVerstuurdOp: string | null;
  ververs: () => Promise<void>;
  /**
   * Mailt de al-vastgestelde factuur naar de partij erachter — het endpoint
   * bepaalt het adres zelf, dit geeft alleen aan wélke factuur. Alleen
   * bruikbaar voor de admin: het endpoint zelf controleert dat.
   */
  verstuurFactuur: (
    factuur: VastgesteldeFactuur,
  ) => Promise<{ ok: boolean; melding: string | null }>;
  /**
   * Verstuurt in één keer alle nog niet verzonden facturen met een bekend
   * e-mailadres — "mail alles automatisch naar de juiste partij", met de
   * admin op de knop.
   */
  verstuurAlleFacturen: () => Promise<{
    aantalVerstuurd: number;
    fouten: { factuurnummer: string; melding: string }[];
  }>;
  /**
   * Vult het ontbrekende e-mailadres van de kraam of organisator achter deze
   * factuur aan — rechtstreeks op hun rij, niet op de factuur (die heeft er
   * geen veld voor). Ververst daarna de afrekening, zodat de knop verschijnt.
   */
  stelEmailIn: (
    factuur: VastgesteldeFactuur,
    email: string,
  ) => Promise<{ ok: boolean; melding: string | null }>;
  /** Een tijdelijke downloadlink (60 seconden) voor de bewaarde PDF. */
  haalPdfUrl: (
    factuur: VastgesteldeFactuur,
  ) => Promise<{ ok: boolean; url: string | null; melding: string | null }>;
  /**
   * Zet de nog niet eerder verstuurde uitbetalingen op "in_batch" — bedoeld
   * om precies één keer aan te roepen zodra het SEPA-bestand echt naar de
   * bank gaat, zodat een tweede download zichtbaar is als herhaling in
   * plaats van stilletjes een nieuwe, ongemarkeerde batch te lijken.
   */
  markeerUitbetalingBatch: () => Promise<{ ok: boolean; melding: string | null }>;
}

/** De eerstvolgende werkdag: uitbetaling heet "binnen één werkdag". */
function eerstvolgendeWerkdag(vanaf: Date): string {
  const dag = new Date(vanaf);
  do {
    dag.setUTCDate(dag.getUTCDate() + 1);
  } while (dag.getUTCDay() === 0 || dag.getUTCDay() === 6);
  return dag.toISOString().slice(0, 10);
}

export function useAfrekening(evenement: Evenement): Afrekeningbeeld {
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState<string | null>(null);
  /** Apart van `fout`: de cijfers kloppen wel, alleen de facturen kunnen niet. */
  const [factuurfout, setFactuurfout] = useState<string | null>(null);
  const [afrekening, setAfrekening] = useState<Evenementafrekening | null>(null);
  const [facturen, setFacturen] = useState<VastgesteldeFactuur[]>([]);
  const [ontvangerPerFactuur, setOntvangerPerFactuur] = useState<Record<string, string[]>>({});
  const [factuurBron, setFactuurBron] = useState<
    Record<string, { tabel: 'kramen' | 'organisatoren'; id: string }>
  >({});
  const [sepa, setSepa] = useState<SepaBestand | null>(null);
  const [uitbetalingen, setUitbetalingen] = useState<VastgesteldeUitbetaling[]>([]);
  const [batchVerstuurdOp, setBatchVerstuurdOp] = useState<string | null>(null);
  const [marge, setMarge] = useState<Marge | null>(null);
  const [inkoopCent, setInkoopCent] = useState(0);

  const ververs = useCallback(async () => {
    const { data: deelnames, error: deelnamefout } = await supabase
      .from('kraamdeelnames')
      .select('id, kraam_id, commissie_bp, uitbater, status')
      .eq('evenement_id', evenement.id);

    if (deelnamefout) {
      setFout('De afrekening kon niet worden opgehaald.');
      setLaden(false);
      return;
    }

    const deelnameLijst = deelnames ?? [];
    const deelnameIds = deelnameLijst.map((deelname) => deelname.id);
    if (deelnameIds.length === 0) {
      setAfrekening(null);
      setLaden(false);
      setFout(null);
      return;
    }

    const [{ data: kraamrijen }, bonrijen, { data: organisatorrijen }] = await Promise.all([
      supabase
        .from('kramen')
        .select('id, naam, iban, kvk_nummer, btw_nummer, email')
        .in(
          'id',
          deelnameLijst.map((deelname) => deelname.kraam_id),
        ),
      haalAlles<{ id: string; kraamdeelname_id: string; statiegeld_cent: number }>((van, tot) =>
        supabase
          .from('bonnen')
          .select('id, kraamdeelname_id, statiegeld_cent')
          .in('kraamdeelname_id', deelnameIds)
          .eq('status', 'afgerond')
          .order('id')
          .range(van, tot),
      ),
      supabase
        .from('organisatoren')
        .select('bedrijfsnaam, iban, kvk_nummer, btw_nummer, email')
        .eq('id', evenement.organisator_id)
        .limit(1),
    ]);

    const bonnen = bonrijen;

    // Filteren gaat via de kraamdeelname, niet via een lijst met bon-ids. Zo'n
    // lijst maakt bij een paar honderd bonnen al een adres dat de server
    // weigert, en op een festivaldag zijn het er duizenden.
    const [regelrijen, correctierijen, betalingrijen, retourrijen] = await Promise.all([
      haalAlles<{ bon_id: string; btw_tarief: number; regeltotaal_cent: number }>((van, tot) =>
        supabase
          .from('bonregels')
          .select('bon_id, btw_tarief, regeltotaal_cent, bonnen!inner(kraamdeelname_id)')
          .in('bonnen.kraamdeelname_id', deelnameIds)
          .order('id')
          .range(van, tot),
      ),
      haalAlles<{ bon_id: string; verschil_cent: number }>((van, tot) =>
        supabase
          .from('correcties')
          .select('bon_id, verschil_cent, bonnen!inner(kraamdeelname_id)')
          .in('bonnen.kraamdeelname_id', deelnameIds)
          .order('id')
          .range(van, tot),
      ),
      haalAlles<{ id: string; bon_id: string }>((van, tot) =>
        supabase
          .from('betalingen')
          .select('id, bon_id, bonnen!inner(kraamdeelname_id)')
          .in('bonnen.kraamdeelname_id', deelnameIds)
          .order('id')
          .range(van, tot),
      ),
      haalAlles<{ betaling_id: string; bedrag_cent: number; status: string }>((van, tot) =>
        supabase
          .from('retouren')
          .select(
            'betaling_id, bedrag_cent, status, betalingen!inner(bonnen!inner(kraamdeelname_id))',
          )
          .in('betalingen.bonnen.kraamdeelname_id', deelnameIds)
          .order('id')
          .range(van, tot),
      ),
    ]);

    const betalingen = betalingrijen;

    const deelnamePerBon = new Map(bonnen.map((bon) => [bon.id, bon.kraamdeelname_id]));
    const deelnamePerBetaling = new Map(
      betalingen.map((betaling) => [betaling.id, deelnamePerBon.get(betaling.bon_id)]),
    );

    const kraamPerId = new Map((kraamrijen ?? []).map((kraam) => [kraam.id, kraam]));
    const organisator = organisatorrijen?.[0];

    const kramen: KraamafrekeningInvoer[] = deelnameLijst.map((deelname) => {
      const kraam = kraamPerId.get(deelname.kraam_id);
      const eigenBonnen = bonnen.filter((bon) => bon.kraamdeelname_id === deelname.id);
      const eigenBonIds = new Set(eigenBonnen.map((bon) => bon.id));

      const perTarief = new Map<BtwTarief, number>();
      for (const regel of regelrijen) {
        if (!eigenBonIds.has(regel.bon_id)) continue;
        const tarief = regel.btw_tarief as BtwTarief;
        perTarief.set(tarief, (perTarief.get(tarief) ?? 0) + regel.regeltotaal_cent);
      }

      const omzetPerTarief: BtwRegel[] = [...perTarief.entries()].map(([tarief, brutoCent]) => ({
        tarief,
        brutoCent,
      }));

      const correctiesCent = correctierijen
        .filter((correctie) => eigenBonIds.has(correctie.bon_id))
        .reduce((som, correctie) => som + correctie.verschil_cent, 0);

      const retourenCent = retourrijen
        .filter(
          (retour) =>
            retour.status === 'geslaagd' &&
            deelnamePerBetaling.get(retour.betaling_id) === deelname.id,
        )
        .reduce((som, retour) => som + retour.bedrag_cent, 0);

      const statiegeldSaldoCent = eigenBonnen.reduce((som, bon) => som + bon.statiegeld_cent, 0);

      const uitbater = (deelname.uitbater ?? 'kraam') as Uitbater;
      // Het tarief per bon staat in tienden van een cent: de som hoort in de kern,
      // niet hier. Zelf vermenigvuldigen gaf tien keer te veel (21 september 2026).
      const transactiekostenCent = berekenKraamTransactiekosten(
        {
          perBonTiendeCent: evenement.transactiekosten_per_bon_tiende_cent,
          overOmzetBasispunten: evenement.transactiekosten_over_omzet_bp,
        },
        {
          aantalBonnen: eigenBonnen.length,
          omzetCent: [...perTarief.values()].reduce((a, b) => a + b, 0),
        },
      );

      return {
        kraamdeelnameId: deelname.id,
        kraamnaam: kraam?.naam ?? 'Kraam',
        iban: uitbater === 'organisator' ? (organisator?.iban ?? '') : (kraam?.iban ?? ''),
        uitbater,
        commissieBasispunten: deelname.commissie_bp,
        omzetPerTarief,
        aantalBonnen: eigenBonnen.length,
        correctiesCent,
        retourenCent,
        statiegeldSaldoCent,
        transactiekostenCent,
        bufferBasispunten: evenement.buffer_bp,
      };
    });

    const berekend = maakEvenementafrekening({
      evenementnaam: evenement.naam,
      organisatornaam: organisator?.bedrijfsnaam ?? 'Organisator',
      organisatorIban: organisator?.iban ?? '',
      prijsmodel: {
        dagtariefPerKraamsetCent: evenement.dagtarief_kraamset_cent,
        perBonTiendeCent: evenement.transactiekosten_per_bon_tiende_cent,
        overOmzetBasispunten: evenement.transactiekosten_over_omzet_bp,
      },
      aantalKraamsets: deelnameLijst.length,
      aantalDagen: evenementdagen(evenement.start_op, evenement.eind_op),
      kramen,
    });

    setAfrekening(berekend);

    // Wat wij zelf aan de betaalpartij kwijt zijn, met de tarieven die bij dit
    // evenement horen. Verandert hun tarief, dan verandert alleen die instelling.
    const inkoopTransactiekostenCent = berekenInkoopkosten(
      {
        perBonTiendeCent: evenement.inkoop_per_bon_tiende_cent,
        overOmzetBasispunten: evenement.inkoop_over_omzet_bp,
      },
      {
        aantalBonnen: berekend.kramen.reduce((som, kraam) => som + kraam.aantalBonnen, 0),
        omzetCent: berekend.kramen.reduce((som, kraam) => som + kraam.brutoOmzetCent, 0),
      },
    );
    setInkoopCent(inkoopTransactiekostenCent);
    setMarge(
      berekenMarge({
        doorbelasteTransactiekostenCent: berekend.transactiekostenTotaalCent,
        inkoopTransactiekostenCent,
        kraamsetkostenNettoCent: berekend.kraamsetkosten.nettoCent,
        eigenKostenCent: evenement.eigen_kosten_cent,
        inhoudingenCent: berekend.inhoudingenTotaalCent,
      }),
    );

    // De facturen zelf komen niet meer uit een lokale berekening: dat gaf
    // "index + 1" als volgnummer, en dus bij elk evenement weer "0001" (zie
    // docs/open-punten.md, punt 6). vaststel-facturen rekent zelf (nooit
    // clientdata vertrouwen voor iets dat een officiële factuur wordt), geeft
    // een echt doorlopend nummer uit en bewaart de PDF — en is idempotent:
    // een tweede aanroep voor hetzelfde evenement maakt geen nieuwe rijen.
    const { data: vaststelling, error: vaststelfout } = await supabase.functions.invoke<{
      facturen: VastgesteldeFactuur[];
      ontvangerPerFactuur: Record<string, string[]>;
      uitbetalingen: VastgesteldeUitbetaling[];
      fouten: string[];
    }>('vaststel-facturen', { body: { evenementId: evenement.id } });

    if (vaststelfout || !vaststelling) {
      setFacturen([]);
      setOntvangerPerFactuur({});
      setFactuurBron({});
      setUitbetalingen([]);
      setBatchVerstuurdOp(null);
      setSepa(null);
      setFactuurfout('De facturen en uitbetalingen konden niet worden opgehaald of vastgesteld.');
    } else {
      setFacturen(vaststelling.facturen);
      setOntvangerPerFactuur(vaststelling.ontvangerPerFactuur);
      setUitbetalingen(vaststelling.uitbetalingen);
      setBatchVerstuurdOp(
        vaststelling.uitbetalingen.find((u) => u.verstuurdOp !== null)?.verstuurdOp ?? null,
      );

      // vaststel-facturen geeft kraamdeelnameId/organisatorId per factuur mee,
      // dus de bron (welke rij het e-mailadres moet krijgen) volgt direct uit
      // de respons — geen aanname nodig over de volgorde van de facturenlijst
      // ten opzichte van deelnameLijst (die klopt sowieso niet: kramen zonder
      // omzet worden overgeslagen, en de organisatorfacturen zitten ertussen).
      const bronnen: Record<string, { tabel: 'kramen' | 'organisatoren'; id: string }> = {};
      for (const vast of vaststelling.facturen) {
        if (vast.kraamdeelnameId) {
          const kraam = kraamPerId.get(
            deelnameLijst.find((d) => d.id === vast.kraamdeelnameId)?.kraam_id ?? '',
          );
          if (kraam) bronnen[vast.factuur.factuurnummer] = { tabel: 'kramen', id: kraam.id };
        } else if (vast.organisatorId) {
          bronnen[vast.factuur.factuurnummer] = { tabel: 'organisatoren', id: vast.organisatorId };
        }
      }
      setFactuurBron(bronnen);
      setFactuurfout(vaststelling.fouten.length === 0 ? null : vaststelling.fouten.join(' '));

      // Het SEPA-bestand komt nu uit de bevroren, al-vastgestelde
      // uitbetalingen in plaats van de live herberekening: zo blijft het
      // bestand altijd exact overeenkomen met wat er in de database als
      // "te betalen" vaststaat, ook als er nadien nog een correctie binnenkomt.
      try {
        const qualityCashless = await haalOnzeGegevens();
        setSepa(
          maakSepaBestand({
            berichtId: `QC-${evenement.id.slice(0, 8).toUpperCase()}`,
            aangemaaktOp: new Date(),
            uitvoerdatum: eerstvolgendeWerkdag(new Date()),
            opdrachtgeverNaam: qualityCashless.naam,
            opdrachtgeverIban: qualityCashless.iban ?? '',
            opdrachten: vaststelling.uitbetalingen.map((uitbetaling) => ({
              kenmerk: uitbetaling.id.slice(0, 8),
              naam: uitbetaling.naam,
              iban: uitbetaling.iban,
              bedragCent: uitbetaling.bedragCent,
              omschrijving:
                uitbetaling.partij === 'kraam'
                  ? `Uitbetaling ${evenement.naam} - ${uitbetaling.naam}`
                  : `Commissie ${evenement.naam}`,
            })),
          }),
        );
      } catch (oorzaak) {
        setSepa(null);
        setFout(
          oorzaak instanceof Error ? oorzaak.message : 'Het SEPA-bestand kon niet worden gemaakt.',
        );
      }
    }

    setLaden(false);
  }, [evenement]);

  useEffect(() => {
    void (async () => {
      await ververs();
    })();
  }, [ververs]);

  /** Verstuurt zonder daarna te verversen — zo hoeft verstuurAlleFacturen niet na elke afzonderlijke factuur opnieuw alles op te halen. */
  const stuurFactuurZonderVerversen = useCallback(
    async (factuur: VastgesteldeFactuur): Promise<{ ok: boolean; melding: string | null }> => {
      const { error } = await supabase.functions.invoke('verstuur-factuur', {
        body: { factuurId: factuur.id },
      });
      if (error) {
        return { ok: false, melding: 'Versturen is mislukt. Probeer het zo nog eens.' };
      }
      return { ok: true, melding: null };
    },
    [],
  );

  const verstuurFactuur = useCallback(
    async (factuur: VastgesteldeFactuur): Promise<{ ok: boolean; melding: string | null }> => {
      const resultaat = await stuurFactuurZonderVerversen(factuur);
      await ververs();
      return resultaat;
    },
    [stuurFactuurZonderVerversen, ververs],
  );

  /**
   * Verstuurt in één keer alle facturen die nog niet zijn verzonden en een
   * bekend e-mailadres hebben — de "mail alles automatisch naar de juiste
   * partij" uit het bouwplan, maar dan met de admin op de knop in plaats van
   * onbemand: uitbetalingen (en dus ook het versturen ervan) zijn een
   * adminzaak, zie de eerdere afweging bij verstuur-factuur zelf.
   */
  const verstuurAlleFacturen = useCallback(async (): Promise<{
    aantalVerstuurd: number;
    fouten: { factuurnummer: string; melding: string }[];
  }> => {
    const teVersturen = facturen.filter(
      (f) => f.status !== 'verstuurd' && ontvangerPerFactuur[f.factuur.factuurnummer] !== undefined,
    );
    const fouten: { factuurnummer: string; melding: string }[] = [];
    let aantalVerstuurd = 0;
    for (const factuur of teVersturen) {
      const resultaat = await stuurFactuurZonderVerversen(factuur);
      if (resultaat.ok) {
        aantalVerstuurd += 1;
      } else {
        fouten.push({
          factuurnummer: factuur.factuur.factuurnummer,
          melding: resultaat.melding ?? 'Onbekende fout.',
        });
      }
    }
    await ververs();
    return { aantalVerstuurd, fouten };
  }, [facturen, ontvangerPerFactuur, stuurFactuurZonderVerversen, ververs]);

  const stelEmailIn = useCallback(
    async (
      factuur: VastgesteldeFactuur,
      email: string,
    ): Promise<{ ok: boolean; melding: string | null }> => {
      const bron = factuurBron[factuur.factuur.factuurnummer];
      if (bron === undefined) {
        return { ok: false, melding: 'Onbekend bij welke partij dit e-mailadres hoort.' };
      }
      const { error } = await supabase.from(bron.tabel).update({ email }).eq('id', bron.id);
      if (error) {
        return { ok: false, melding: 'Opslaan is mislukt. Probeer het zo nog eens.' };
      }
      void logAudit('email_gewijzigd', bron.tabel, bron.id, {
        factuurnummer: factuur.factuur.factuurnummer,
        email,
      });
      await ververs();
      return { ok: true, melding: null };
    },
    [factuurBron, ververs],
  );

  const haalPdfUrl = useCallback(
    async (
      factuur: VastgesteldeFactuur,
    ): Promise<{ ok: boolean; url: string | null; melding: string | null }> => {
      if (!factuur.pdfPad) {
        return { ok: false, url: null, melding: 'Er is nog geen PDF voor deze factuur.' };
      }
      // Toegang loopt via dezelfde RLS als de facturen-tabel zelf (zie de
      // storage-policy in de migratie): een organisator of kraam kan hier dus
      // ook zelf terechtkomen zonder dat wij zelf iets hoeven te checken.
      const { data, error } = await supabase.storage
        .from('facturen')
        .createSignedUrl(factuur.pdfPad, 60);
      if (error || !data) {
        return { ok: false, url: null, melding: 'De PDF kon niet worden opgehaald.' };
      }
      return { ok: true, url: data.signedUrl, melding: null };
    },
    [],
  );

  const markeerUitbetalingBatch = useCallback(async (): Promise<{
    ok: boolean;
    melding: string | null;
  }> => {
    // Alleen wat écht in het bestand zat: een overgeslagen uitbetaling (geen of
    // fout IBAN) mag niet als verstuurd te boek komen te staan.
    const opgenomen = new Set(sepa?.opgenomen ?? []);
    const idsTeMarkeren = uitbetalingen
      .filter((u) => u.status === 'concept' && opgenomen.has(u.id.slice(0, 8)))
      .map((u) => u.id);
    if (idsTeMarkeren.length === 0) return { ok: true, melding: null };
    const batchId = `QC-${evenement.id.slice(0, 8).toUpperCase()}`;
    const { error } = await supabase
      .from('uitbetalingen')
      .update({ status: 'in_batch', batch_id: batchId, verstuurd_op: new Date().toISOString() })
      .in('id', idsTeMarkeren);
    if (error) {
      return { ok: false, melding: 'Kon de uitbetalingen niet als verstuurd markeren.' };
    }
    void logAudit('uitbetalingsbatch_verstuurd', 'uitbetalingen', evenement.id, {
      batchId,
      aantal: idsTeMarkeren.length,
    });
    await ververs();
    return { ok: true, melding: null };
  }, [uitbetalingen, sepa, evenement.id, ververs]);

  return {
    laden,
    fout,
    factuurfout,
    afrekening,
    marge,
    inkoopTransactiekostenCent: inkoopCent,
    facturen,
    ontvangerPerFactuur,
    sepa,
    uitbetalingen,
    batchVerstuurdOp,
    ververs,
    verstuurFactuur,
    verstuurAlleFacturen,
    stelEmailIn,
    haalPdfUrl,
    markeerUitbetalingBatch,
  };
}
